import { i18n_t as $t } from '@utils/i18n';

export const maxDurationOptions = () => {
    return [
        {
            id: 'hours',
            label: $t('Hours'),
            options: [
                {
                    label: $t('workflowsConstants.OneHour'),
                    id: 3600,
                    icon: {
                        type: 'clock',
                    },
                },
                {
                    label: $t('workflowsConstants.ThreeHours'),
                    id: 10800,
                    icon: {
                        type: 'clock',
                    },
                },
                {
                    label: $t('workflowsConstants.EightHours'),
                    id: 28800,
                    icon: {
                        type: 'clock',
                    },
                },
                {
                    label: $t('workflowsConstants.TwelveHours'),
                    id: 43200,
                    icon: {
                        type: 'clock',
                    },
                },
            ],
        },
        {
            id: 'days',
            label: $t('Days'),
            options: [
                {
                    label: $t('workflowsConstants.OneDay'),
                    id: 86400,
                    icon: {
                        type: 'clock',
                    },
                },
                {
                    label: $t('workflowsConstants.SevenDays'),
                    id: 604800,
                    icon: {
                        type: 'clock',
                    },
                },
                {
                    label: $t('workflowsConstants.FourteenDays'),
                    id: 1209600,
                    icon: {
                        type: 'clock',
                    },
                },
                {
                    label: $t('workflowsConstants.ThirtyDays'),
                    id: 2592000,
                    icon: {
                        type: 'clock',
                    },
                },
                {
                    label: $t('workflowsConstants.SixtyDays'),
                    id: 5184000,
                    icon: {
                        type: 'clock',
                    },
                },
                {
                    label: $t('workflowsConstants.NinetyDays'),
                    id: 7776000,
                    icon: {
                        type: 'clock',
                    },
                },
            ],
        },
    ];
};

/**
 * All settings for workflows in a sub are stored in the user settings module under the `workflowsSettings--${subId}` key.
 * The value is an object where each key/value pair is the workflow id (key) and the workflow settings (value).
 *
 * {
 *    name: `workflowsSettings--${subId}`
 *    value: {
 *       [workflowId]: {
 *         dataSource: 'some value',
 *         timePeriod: 'some value',
 *         totalChartDataSource: 'some value',
 *         guideMonitorOverTimeChart: 'some value',
 *         timeToCompleteChartDataType: 'some value',
 *         viewBy: {},
 *         baselineDateRange: <a date range config object>
 *       },
 *       ...other
 *    }
 * }
 */
export const WORKFLOWS_SUB_USER_SETTING_NAME = 'workflowsSettings';

export const WORKFLOW_USER_SETTINGS = {
    DATA_SOURCE: 'dataSource',
    TIME_PERIOD: 'timePeriod',
    TOTAL_CHART_DATA_SOURCE: 'totalChartDataSource',
    GUIDE_MONITOR_OVER_TIME_CHART: 'guideMonitorOverTimeChart',
    TIME_TO_COMPLETE_CHART_DATA_TYPE: 'timeToCompleteChartDataType',
    TIME_TO_COMPLETE_CHART_TIME_PERIOD: 'timeToCompleteChartTimePeriod',
    VIEW_BY: 'viewBy',
    BASELINE_DATE_RANGE: 'baselineDateRange',
};

export const WORKFLOW_VISUALS = {
    // Multi-agg
    HIGHLIGHTS: 'WorkflowHighlights',
    TOTAL_METRICS: 'WorkflowTotalMetrics',
    OVER_TIME: 'WorkflowOverTime',
    TIME_TO_COMPLETE_CHART: 'WorkflowTimeToCompleteChart',
    TIME_TO_COMPLETE_SUMMARY: 'WorkflowTimeToCompleteSummary',
    // Helper single-agg for OVER_TIME_PERIODS
    OVER_TIME_PERIODS: 'WorkflowOverTimePeriods',
    // Single-agg
    HIGHLIGHTS_BASELINE_COMPARISON: 'WorkflowHighlightsBaselineComparison',
    // Single-agg
    VISITOR_LIST: 'WorkflowVisitorList',
    // Single-agg
    COMPLETED_JOURNEYS: 'CompletedJourneys',
};

export const WORKFLOW_MULTI_AGG_STATUS_PROP = 'multiAggStatus';

export const WORKFLOW_MULTI_AGG_DATA_PROP = 'multiAggData';

export const WORKFLOW_HIGHLIGHTS_BASELINE_COMPARISON_AGG_STATUS_PROP = 'highlightsBaselineComparisonStatus';

export const WORKFLOW_HIGHLIGHTS_BASELINE_COMPARISON_AGG_DATA_PROP = 'highlightsBaselineComparisonData';

export const WORKFLOW_VISITOR_LIST_AGG_STATUS_PROP = 'visitorListStatus';

export const WORKFLOW_VISITOR_LIST_AGG_DATA_PROP = 'visitorListData';

export const COMPLETED_JOURNEYS_AGG_STATUS_PROP = 'completedJourneysStatus';

export const COMPLETED_JOURNEYS_AGG_DATA_PROP = 'completedJourneysData';

export const WORKFLOW_VISUALS_DATA = {
    [WORKFLOW_VISUALS.HIGHLIGHTS]: `${WORKFLOW_MULTI_AGG_DATA_PROP}.${WORKFLOW_VISUALS.HIGHLIGHTS}`,
    [WORKFLOW_VISUALS.TOTAL_METRICS]: `${WORKFLOW_MULTI_AGG_DATA_PROP}.${WORKFLOW_VISUALS.TOTAL_METRICS}`,
    [WORKFLOW_VISUALS.OVER_TIME]: `${WORKFLOW_MULTI_AGG_DATA_PROP}.${WORKFLOW_VISUALS.OVER_TIME}`,
    [WORKFLOW_VISUALS.TIME_TO_COMPLETE_CHART]: `${WORKFLOW_MULTI_AGG_DATA_PROP}.${WORKFLOW_VISUALS.TIME_TO_COMPLETE_CHART}`,
    [WORKFLOW_VISUALS.TIME_TO_COMPLETE_SUMMARY]: `${WORKFLOW_MULTI_AGG_DATA_PROP}.${WORKFLOW_VISUALS.TIME_TO_COMPLETE_SUMMARY}`,
    [WORKFLOW_VISUALS.HIGHLIGHTS_BASELINE_COMPARISON]: `${WORKFLOW_HIGHLIGHTS_BASELINE_COMPARISON_AGG_DATA_PROP}`,
    [WORKFLOW_VISUALS.VISITOR_LIST]: `${WORKFLOW_VISITOR_LIST_AGG_DATA_PROP}`,
    [WORKFLOW_VISUALS.COMPLETED_JOURNEYS]: `${COMPLETED_JOURNEYS_AGG_DATA_PROP}`,
};

export const WORKFLOW_VISUALS_AGG_STATUS = {
    [WORKFLOW_VISUALS.HIGHLIGHTS]: `${WORKFLOW_MULTI_AGG_DATA_PROP}.${WORKFLOW_VISUALS.HIGHLIGHTS}`,
    [WORKFLOW_VISUALS.TOTAL_METRICS]: `${WORKFLOW_MULTI_AGG_DATA_PROP}.${WORKFLOW_VISUALS.TOTAL_METRICS}`,
    [WORKFLOW_VISUALS.OVER_TIME]: `${WORKFLOW_MULTI_AGG_DATA_PROP}.${WORKFLOW_VISUALS.OVER_TIME}`,
    [WORKFLOW_VISUALS.TIME_TO_COMPLETE_CHART]: `${WORKFLOW_MULTI_AGG_DATA_PROP}.${WORKFLOW_VISUALS.TIME_TO_COMPLETE_CHART}`,
    [WORKFLOW_VISUALS.VISITOR_LIST]: `${WORKFLOW_VISITOR_LIST_AGG_DATA_PROP}.${WORKFLOW_VISUALS.VISITOR_LIST}`,
    [WORKFLOW_VISUALS.COMPLETED_JOURNEYS]: `${COMPLETED_JOURNEYS_AGG_DATA_PROP}.${WORKFLOW_VISUALS.COMPLETED_JOURNEYS}`,
};

export const WORKFLOW_CHART_TYPE_NAME_MAP = {
    WorkflowTotalMetrics: 'Visitor completion rate',
    WorkflowTimeToCompleteChart: 'Time to complete',
    WorkflowVisitorList: 'Visitor completion',
    CompletedJourneys: 'Journeys',
};

export const DEFAULT_WORKFLOW_MAX_DURATION = 86400;

export const DEFAULT_WORKFLOW_MAX_INTERVAL = 604800;

export const DEFAULT_WORKFLOW_MAX_PATH_LENGTH = 20;
